import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, Crown, Share2, Sparkles, Tv } from "lucide-react";
import { toast } from "sonner";
import { PopButton } from "../PopButton";
import { Panel } from "../PageShell";
import { resultSize } from "../reveal/ResultReveal";
import { groupShareLink } from "../../lib/share";

export interface GroupOption { id: string; label: string }
export interface GroupRound { number: number; tally: Record<string, number>; winner_option_id: string; tied_option_ids: string[]; dither_tiebreak: boolean; by_timer?: boolean }
export interface GroupState {
  id: string; title: string; mode: "normal" | "best_of_3"; options: GroupOption[]; host_name: string;
  status: "voting" | "finished"; current_round: number; rounds: GroupRound[]; winner_option_id: string | null; final_tiebreak_ids: string[];
  participants: { id: string; name: string; voted: boolean }[]; live_tally: Record<string, number>; vote_count: number;
  me: { id: string; name: string; vote: string | null } | null; expires_at: string;
  time_limit_sec: number; round_deadline: string | null; server_now: string; sound_style: "gameshow" | "arcade" | "orchestra";
}

export const labelOf = (g: GroupState, id?: string | null) => g.options.find((o) => o.id === id)?.label || "?";

export function InvitePanel({ group }: { group: GroupState }) {
  const [copied, setCopied] = useState(false);
  const url = groupShareLink(group.id);
  const copy = async () => {
    await navigator.clipboard?.writeText(url).catch(() => undefined);
    setCopied(true);
    toast.success("Invite link copied");
    setTimeout(() => setCopied(false), 1500);
  };
  const share = () => (navigator as any).share?.({ title: "Vote on DITHER", text: `Help decide: ${group.title}`, url }).catch(() => undefined);
  return (
    <div className="flex items-center gap-2 rounded-2xl border-2 border-dashed border-ink bg-pop-yellow/40 p-3" data-testid="group-invite-panel">
      <span className="min-w-0 flex-1 truncate text-sm font-bold" data-testid="group-invite-link">{url}</span>
      <PopButton tone="white" size="sm" onClick={copy} data-testid="group-copy-link-button">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</PopButton>
      {"share" in navigator && <PopButton tone="teal" size="sm" onClick={share} data-testid="group-share-link-button"><Share2 className="h-4 w-4" /></PopButton>}
    </div>
  );
}

export function Participants({ group }: { group: GroupState }) {
  return (
    <div>
      <p className="mb-2 text-xs font-extrabold uppercase tracking-widest text-zinc-500">{group.participants.length} joined · {group.vote_count} voted</p>
      <div className="flex flex-wrap gap-2" data-testid="group-participants">
        {group.participants.map((p) => (
          <span key={p.id} className={`chip inline-flex max-w-[12rem] items-center gap-1 ${p.voted ? "bg-pop-green text-white" : ""}`} data-testid="group-participant-chip">
            {p.voted && <Check className="h-3.5 w-3.5" strokeWidth={3} />}<span className="truncate">{p.name}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export function TallyBars({ group, tally }: { group: GroupState; tally: Record<string, number> }) {
  const max = Math.max(1, ...Object.values(tally));
  return (
    <div className="space-y-2" data-testid="group-tally">
      {group.options.map((o) => (
        <div key={o.id} className="flex items-center gap-3">
          <span className="w-1/3 truncate text-sm font-bold">{o.label}</span>
          <div className="h-5 flex-1 overflow-hidden rounded-full border-2 border-ink bg-zinc-100">
            <div className="h-full bg-pop-teal transition-[width] duration-300" style={{ width: `${((tally[o.id] || 0) / max) * 100}%` }} />
          </div>
          <span className="w-6 text-right font-display font-bold">{tally[o.id] || 0}</span>
        </div>
      ))}
    </div>
  );
}

export function SeriesTracker({ group }: { group: GroupState }) {
  if (group.mode !== "best_of_3") return null;
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="group-series-tracker">
      {[1, 2, 3].map((n) => {
        const r = group.rounds.find((x) => x.number === n);
        const live = !r && n === group.current_round && group.status === "voting";
        return (
          <div key={n} className={`rounded-xl border-2 border-ink p-2 text-center ${r ? "bg-pop-yellow" : live ? "bg-white" : "bg-zinc-100 opacity-60"}`} data-testid={`group-round-${n}`}>
            <p className="text-[10px] font-extrabold uppercase tracking-widest">Round {n}</p>
            <p className="truncate text-sm font-bold">{r ? labelOf(group, r.winner_option_id) : live ? "Voting…" : "—"}</p>
            {r?.dither_tiebreak && <p className="text-[10px] font-extrabold uppercase text-pop-purple">Dither tiebreak</p>}
            {r?.by_timer && <p className="text-[10px] font-extrabold uppercase text-zinc-500">Time's up</p>}
          </div>
        );
      })}
    </div>
  );
}

export function VotePanel({ group, onVote, busy }: { group: GroupState; onVote: (id: string) => void; busy: boolean }) {
  const myVote = group.me?.vote;
  return (
    <div className="space-y-3" data-testid="group-vote-panel">
      <p className="font-display text-xl font-bold uppercase">{myVote ? "Vote locked in" : `Your vote, ${group.me?.name}`}{group.mode === "best_of_3" && ` · Round ${group.current_round}`}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {group.options.map((o, i) => (
          <button key={o.id} disabled={!!myVote || busy} onClick={() => onVote(o.id)} data-testid={`group-vote-option-${i}`} data-active={myVote === o.id}
            className="btn-pop rounded-2xl bg-white px-4 py-4 text-left font-display text-lg font-bold [overflow-wrap:anywhere] data-[active=true]:bg-pop-orange data-[active=true]:text-white data-[active=true]:!opacity-100">
            {o.label}
          </button>
        ))}
      </div>
      {myVote && <p className="text-sm font-semibold text-zinc-600" data-testid="group-voted-message">One vote per person per round. Waiting for the host to reveal…</p>}
    </div>
  );
}

export function WinnerPanel({ group, onShare, onReveal }: { group: GroupState; onShare: () => void; onReveal: () => void }) {
  const winner = labelOf(group, group.winner_option_id);
  const last = group.rounds[group.rounds.length - 1];
  const tied = group.final_tiebreak_ids.length ? group.final_tiebreak_ids : last?.dither_tiebreak ? last.tied_option_ids : [];
  return (
    <div className="rays relative overflow-hidden rounded-[28px] border-[4px] border-ink p-5 pop-shadow-lg sm:p-8" data-testid="group-winner-panel">
      <div className="rounded-[22px] border-[3px] border-ink bg-white p-6 text-center">
        <span className="inline-flex -rotate-2 items-center gap-1 rounded-full border-[3px] border-ink bg-pop-pink px-4 py-1 font-display text-lg font-bold uppercase text-white"><Crown className="h-5 w-5" />The group says</span>
        <h2 className={`comic-title mt-4 text-pop-orange [overflow-wrap:anywhere] ${resultSize(winner)}`} data-testid="group-winner-label">{winner}</h2>
        {tied.length > 1 && (
          <p className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-pop-purple" data-testid="group-tiebreak-note">
            <Sparkles className="h-4 w-4" />DITHER broke a tie between {tied.map((t) => labelOf(group, t)).join(", ")}
          </p>
        )}
        <PopButton tone="purple" className="mt-6 w-full" onClick={onReveal} data-testid="group-wheel-reveal-button"><Tv className="h-4 w-4" />Big-screen wheel reveal</PopButton>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <PopButton tone="teal" onClick={onShare} data-testid="group-share-result-button"><Share2 className="h-4 w-4" />Share</PopButton>
          <Link to="/group/new" className="btn-pop inline-flex items-center justify-center rounded-2xl bg-pop-yellow px-4 py-3 font-display font-bold uppercase" data-testid="group-new-decision-link">New vote</Link>
        </div>
      </div>
    </div>
  );
}

export function GoneCard({ message }: { message: string }) {
  return (
    <Panel className="text-center" >
      <div data-testid="group-gone-card">
        <p className="font-display text-3xl font-bold uppercase">Poof!</p>
        <p className="mt-2 font-semibold text-zinc-600" data-testid="group-gone-message">{message}</p>
        <Link to="/group/new" className="btn-pop mt-6 inline-flex rounded-2xl bg-pop-purple px-5 py-3 font-display font-bold uppercase text-white" data-testid="group-gone-create-link">Start a new decision</Link>
      </div>
    </Panel>
  );
}
